import React, { useState, useRef } from 'react';
import { Camera, Upload, Image as ImageIcon, Sparkles, X } from 'lucide-react';
import { analyzeRoomVibe } from '../services/geminiService';
import { AnalysisState } from '../types';
import ReactMarkdown from 'react-markdown';

const VibeReader: React.FC = () => {
  const [image, setImage] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<string>('');
  const [status, setStatus] = useState<AnalysisState>(AnalysisState.IDLE);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setImage(base64String);
        setAnalysis('');
        setStatus(AnalysisState.IDLE);
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerAnalysis = async () => {
    if (!image) return;

    setStatus(AnalysisState.ANALYZING);
    try {
      // Extract the base64 data part and mime type
      // Format: data:image/jpeg;base64,....
      const match = image.match(/^data:(.*);base64,(.*)$/);
      if (!match) throw new Error("Invalid image format");
      
      const mimeType = match[1];
      const base64Data = match[2];

      const result = await analyzeRoomVibe(base64Data, mimeType);
      setAnalysis(result || "No analysis returned.");
      setStatus(AnalysisState.COMPLETE);
    } catch (error) {
      console.error(error);
      setStatus(AnalysisState.ERROR);
    }
  };

  const clearImage = () => {
    setImage(null);
    setAnalysis('');
    setStatus(AnalysisState.IDLE);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="max-w-xl mx-auto px-6 pb-20">
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Vibe Check</h1>
        <p className="text-nunchi-600">Upload a photo of a room, meeting, or gathering. <br/>AI will analyze the atmosphere (Kibun).</p>
      </header>

      <div className="bg-white rounded-2xl shadow-sm border border-nunchi-200 overflow-hidden">
        {!image ? (
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="aspect-video bg-nunchi-50 flex flex-col items-center justify-center cursor-pointer hover:bg-nunchi-100 transition-colors border-b border-nunchi-100"
          >
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm text-nunchi-400">
              <Camera size={32} />
            </div>
            <p className="font-medium text-nunchi-600">Tap to upload photo</p>
            <p className="text-xs text-nunchi-400 mt-2">Supports JPG, PNG</p>
          </div>
        ) : (
          <div className="relative">
            <img src={image} alt="To analyze" className="w-full h-auto max-h-[400px] object-cover" />
            <button 
              onClick={clearImage}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div className="p-6">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept="image/*" 
            className="hidden" 
          />

          {status === AnalysisState.IDLE && (
            <button 
              onClick={triggerAnalysis}
              disabled={!image}
              className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                image 
                  ? 'bg-nunchi-700 text-white hover:bg-nunchi-800 shadow-md' 
                  : 'bg-nunchi-100 text-nunchi-300 cursor-not-allowed'
              }`}
            >
              <Sparkles size={18} /> Analyze Atmosphere
            </button>
          )}

          {status === AnalysisState.ANALYZING && (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-nunchi-200 border-t-nunchi-600 rounded-full animate-spin mb-3"></div>
              <p className="text-nunchi-600 animate-pulse">Reading the room...</p>
            </div>
          )}

          {status === AnalysisState.ERROR && (
             <div className="text-center py-4 text-red-500">
               <p>Failed to analyze image. Try a different one.</p>
               <button onClick={triggerAnalysis} className="mt-2 text-sm underline">Try Again</button>
             </div>
          )}

          {status === AnalysisState.COMPLETE && (
            <div className="animate-fadeIn">
              <h3 className="text-xs font-bold text-nunchi-500 uppercase tracking-widest mb-3">
                Atmosphere Report
              </h3>
              <div className="prose prose-sm prose-slate text-nunchi-800">
                <ReactMarkdown>{analysis}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 bg-blue-50 p-4 rounded-xl flex gap-3 text-sm text-blue-800 border border-blue-100">
        <ImageIcon className="flex-shrink-0" size={20} />
        <p>
          <strong>Tip:</strong> Try uploading images from movies, stock photos of meetings, or group dinners to practice spotting power dynamics before the AI tells you.
        </p>
      </div>
    </div>
  );
};

export default VibeReader;